import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  PlannableUser,
  ResourceAssignment,
  ResourceAssignmentPayload,
  ResourceCategoryDef,
  WORK_STATUSES
} from '../../../Models/resource-assignment.model';
import { Customer } from '../../../Models/customer.model';
import { Equipment } from '../../../Models/equipment.model';
import { Maintenance } from '../../../Models/maintenance.model';
import { CustomerService } from '../../../Services/customer.service';
import { EquipmentService } from '../../../Services/equipment.service';
import { MaintenanceService } from '../../../Services/maintenance.service';

export interface AssignmentDialogData {
  users: PlannableUser[];
  categories: ResourceCategoryDef[];
  assignment?: ResourceAssignment;
  defaultUserId?: string;
  defaultStart?: Date;
  defaultEnd?: Date;
  defaultTitle?: string;
  defaultNotes?: string;
  defaultSendInvite?: boolean;
  defaultSourceUid?: string;
}

export interface AssignmentDialogResult {
  action: 'save' | 'delete';
  payload?: ResourceAssignmentPayload;
  sendCancellation?: boolean;
  deleteSeries?: boolean;
}

const PRESET_COLORS = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c2185b', '#00838f', '#5d4037'];

@Component({
  selector: 'app-assignment-dialog',
  templateUrl: './assignment-dialog.component.html',
  styleUrls: ['./assignment-dialog.component.css']
})
export class AssignmentDialogComponent implements OnInit {
  isEdit = false;
  presetColors = PRESET_COLORS;
  categories: ResourceCategoryDef[] = [];
  workStatuses = WORK_STATUSES;

  assignedUserId = '';
  participantUserIds: string[] = [];
  title = '';
  categoryId: number | null = null;
  workStatus = 'Planned';
  colorTouched = false;
  startLocal = '';
  endLocal = '';
  location = '';
  customerId: number | null = null;
  equipmentId: number | null = null;
  notes = '';
  color = PRESET_COLORS[0];
  sendInvite = true;
  sourceExternalUid: string | null = null;
  travelMinutesBefore = 0;
  travelMinutesAfter = 0;

  // Wiederholung (nur Neuanlage)
  repeatMode = 'none';
  repeatCount = 4;

  customers: Customer[] = [];
  equipments: Equipment[] = [];
  maintenances: Maintenance[] = [];
  maintenanceId: number | null = null;
  loadingEquipments = false;

  constructor(
    private dialogRef: MatDialogRef<AssignmentDialogComponent, AssignmentDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: AssignmentDialogData,
    private customerService: CustomerService,
    private equipmentService: EquipmentService,
    private maintenanceService: MaintenanceService
  ) { }

  ngOnInit(): void {
    this.categories = this.data.categories || [];
    this.customerService.getCustomers().subscribe(c => this.customers = c || []);

    if (this.data.assignment) {
      this.isEdit = true;
      const a = this.data.assignment;
      this.assignedUserId = a.assignedUserId;
      this.participantUserIds = (a.participantUserIds || []).slice();
      this.title = a.title;
      this.categoryId = a.categoryId ?? this.firstCategoryId();
      this.startLocal = this.toLocalInput(a.start);
      this.endLocal = this.toLocalInput(a.end);
      this.location = a.location || '';
      this.customerId = a.customerId ?? null;
      this.equipmentId = a.equipmentId ?? null;
      this.maintenanceId = a.maintenanceId ?? null;
      this.notes = a.notes || '';
      this.color = a.color || this.categoryColorById(this.categoryId);
      this.colorTouched = true;
      this.sendInvite = false;
      this.sourceExternalUid = a.sourceExternalUid ?? null;
      this.travelMinutesBefore = a.travelMinutesBefore ?? 0;
      this.travelMinutesAfter = a.travelMinutesAfter ?? 0;
      this.workStatus = a.workStatus || 'Planned';
      if (this.customerId) {
        this.loadEquipments(this.customerId);
      }
      if (this.equipmentId) {
        this.loadMaintenances(this.equipmentId);
      }
    } else {
      this.assignedUserId = this.data.defaultUserId || (this.data.users[0]?.id ?? '');
      const start = this.data.defaultStart ? new Date(this.data.defaultStart) : new Date();
      if (!this.data.defaultStart) {
        start.setHours(8, 0, 0, 0);
      }
      const end = this.data.defaultEnd
        ? new Date(this.data.defaultEnd)
        : new Date(start.getTime() + 60 * 60 * 1000);
      this.startLocal = this.dateToLocalInput(start);
      this.endLocal = this.dateToLocalInput(end);
      this.title = this.data.defaultTitle?.trim() || 'Einplanung';
      this.notes = this.data.defaultNotes?.trim() || '';
      this.categoryId = this.firstCategoryId();
      this.color = this.categoryColorById(this.categoryId);
      this.sendInvite = this.data.defaultSendInvite ?? true;
      this.sourceExternalUid = this.data.defaultSourceUid ?? null;
    }
  }

  private firstCategoryId(): number | null {
    return this.categories.length ? this.categories[0].id : null;
  }

  private categoryColorById(id: number | null): string {
    const found = this.categories.find(c => c.id === id);
    return found ? found.color : PRESET_COLORS[0];
  }

  onCategoryChange(): void {
    if (!this.colorTouched) {
      this.color = this.categoryColorById(this.categoryId);
    }
  }

  pickColor(c: string): void {
    this.color = c;
    this.colorTouched = true;
  }

  onCustomerChange(): void {
    this.equipmentId = null;
    this.equipments = [];
    this.maintenanceId = null;
    this.maintenances = [];
    if (this.customerId) {
      this.loadEquipments(this.customerId);
    }
  }

  onEquipmentChange(): void {
    this.maintenanceId = null;
    this.maintenances = [];
    if (this.equipmentId) {
      this.loadMaintenances(this.equipmentId);
    }
  }

  onMaintenanceChange(): void {
    const m = this.maintenances.find(x => x.id === this.maintenanceId);
    // Titel automatisch übernehmen, wenn noch Standard/leer.
    if (m && (!this.title.trim() || this.title.trim() === 'Einplanung')) {
      this.title = m.title;
    }
  }

  private loadEquipments(customerId: number): void {
    this.loadingEquipments = true;
    this.equipmentService.getEquipmentsByCustomer(customerId).subscribe({
      next: e => { this.equipments = e || []; this.loadingEquipments = false; },
      error: () => { this.equipments = []; this.loadingEquipments = false; }
    });
  }

  private loadMaintenances(equipmentId: number): void {
    this.maintenanceService.getMaintenancesByEquipment(equipmentId).subscribe({
      next: m => this.maintenances = m || [],
      error: () => this.maintenances = []
    });
  }

  get isValid(): boolean {
    return !!this.assignedUserId
      && !!this.title.trim()
      && !!this.startLocal
      && !!this.endLocal
      && new Date(this.endLocal) > new Date(this.startLocal);
  }

  save(): void {
    if (!this.isValid) {
      return;
    }
    const payload: ResourceAssignmentPayload = {
      assignedUserId: this.assignedUserId,
      participantUserIds: this.participantUserIds.filter(id => id !== this.assignedUserId),
      title: this.title.trim(),
      categoryId: this.categoryId,
      start: this.localInputToIso(this.startLocal),
      end: this.localInputToIso(this.endLocal),
      location: this.location.trim() || undefined,
      customerId: this.customerId,
      equipmentId: this.equipmentId,
      maintenanceId: this.maintenanceId,
      notes: this.notes.trim() || undefined,
      color: this.color,
      sourceExternalUid: this.sourceExternalUid,
      travelMinutesBefore: this.travelMinutesBefore || 0,
      travelMinutesAfter: this.travelMinutesAfter || 0,
      workStatus: this.workStatus,
      sendInvite: this.sendInvite
    };
    if (!this.isEdit && this.repeatMode !== 'none') {
      payload.repeatMode = this.repeatMode;
      payload.repeatCount = this.repeatCount;
    }
    this.dialogRef.close({ action: 'save', payload });
  }

  get isSeries(): boolean {
    return !!this.data.assignment?.seriesId;
  }

  delete(): void {
    const sendCancellation = !!this.data.assignment?.inviteSent
      && confirm('Termin löschen und dem Mitarbeiter eine Absage an Outlook senden?');
    let deleteSeries = false;
    if (this.isSeries) {
      deleteSeries = confirm('Dieser Termin gehört zu einer Serie. Ganze Serie löschen? (Abbrechen = nur diesen Termin)');
    }
    this.dialogRef.close({ action: 'delete', sendCancellation, deleteSeries });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  // Hilfsfunktionen: das Backend arbeitet mit lokaler Wandzeit (ohne Zeitzonen-Offset).
  private toLocalInput(iso: string): string {
    return iso ? iso.substring(0, 16) : '';
  }

  private dateToLocalInput(d: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private localInputToIso(local: string): string {
    return local.length === 16 ? `${local}:00` : local;
  }
}
